import { listUserTenants } from 'account/components/userManagement/tenants/new-actions'
import { userTenantsSelector } from 'account/components/userManagement/tenants/selectors'
import ApiClient from 'api-client/ApiClient'
import { getDomainName } from 'api-client/helpers'
import { setupKappiSession } from 'app/plugins/account/components/userManagement/users/new-actions'
import { cacheActions } from 'core/caching/cacheReducers'
import useListAction from 'core/hooks/useListAction'
import { sessionActions, SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { head, prop, propEq } from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import GlobalDropdownWithSearch, { Item } from '../dropdown/GlobalDropdownWithSearch'

const GlobalTenantDropdown = () => {
  const { keystone } = ApiClient.getInstance()
  const { prefs, updatePrefs } = useScopedPreferences()
  const { currentTenant, currentTenantName } = prefs

  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { isSsoToken, unscopedToken } = session
  const dispatch = useDispatch()

  const { loading: loadingTenants, reload: reloadTenants } = useListAction(listUserTenants)
  const tenants = useSelector(userTenantsSelector)

  // Dropdown options
  const dropdownOptions: Item[] = useMemo(() => {
    return tenants.map((tenant) => ({
      label: tenant.name,
      value: tenant.id,
    }))
  }, [tenants])

  // Set currentTenant in prefs on first-ever load
  useEffect(() => {
    // If tenants are not loaded, do nothing
    if (!tenants?.length) {
      return
    }

    // If currentTenant is not set, set it or it will default to the first tenant
    const currentIsValid = tenants?.some((t) => t?.id === currentTenant)
    if (!currentTenant || !currentTenantName || !currentIsValid) {
      const defaultTenant = tenants?.find(propEq('name', 'service')) || head(tenants) // fallback to first one

      updatePrefs({
        currentTenant: defaultTenant?.id,
        currentTenantName: defaultTenant?.name,
      })
    }
  }, [tenants])

  const updateCurrentTenant = async (tenant: Item) => {
    // const tenant = tenants?.find(propEq('name', tenantName))
    if (!tenant?.value || !tenant?.label) {
      return
    }

    const { user, role, scopedToken } = await keystone.changeProjectScopeWithToken(
      tenant.value,
      isSsoToken,
    )

    // Find the tenant in tenants list
    const matchedTenant = tenants?.find(propEq('id', tenant.value))

    // Refetch dex token
    await setupKappiSession({
      username: '',
      password: unscopedToken,
      domain: getDomainName(),
      activeTenant: matchedTenant,
    })

    dispatch(cacheActions.clearCache())
    dispatch(sessionActions.resetSession({})) // Reset session params when tenant changes
    dispatch(
      sessionActions.updateSession({
        userDetails: { ...user, role },
        scopedToken,
        activeTenant: tenant.label,
        vmsSearchParams: null,
      }),
    )

    // Create Appication Credential for tenant-user
    // if (isKaapiEnabled() && isKaapiTokenAvailable()) {
    //   createKaapiApplicationCredentials(ApplicationCredentialType.IdentityRef)
    // }
  }

  const handleTenantSelect = useCallback(
    async (newTenant: Item) => {
      if (!newTenant?.value || !newTenant?.label) {
        return
      }

      // Set loadingTenant to true
      dispatch(
        sessionActions.updateSession({
          loadingTenant: true,
        }),
      )

      await updateCurrentTenant(newTenant)
      updatePrefs({
        currentTenant: newTenant?.value,
        currentTenantName: newTenant?.label,
        allTenants: false, //Reset AllTenants to false when tenant is changed
      })

      // Set loadingTenant to false
      dispatch(
        sessionActions.updateSession({
          loadingTenant: false,
        }),
      )
    },
    [tenants],
  )

  return (
    <GlobalDropdownWithSearch
      label="Tenant"
      items={dropdownOptions}
      value={String(currentTenant)}
      onChange={handleTenantSelect}
      loading={loadingTenants}
    />
  )
}

export default GlobalTenantDropdown
